<template>
	<div class="theme-toggle-wrap">
		<transition name="theme-hint-fade">
			<div v-if="showHint" class="theme-toggle-hint" role="dialog" aria-labelledby="theme-hint-title">
				<span class="theme-toggle-hint__arrow" aria-hidden="true" />
				<p id="theme-hint-title" class="theme-toggle-hint__title">Choose your display mode</p>
				<p class="theme-toggle-hint__lead">
					Pick what feels best. You can change this anytime from here.
				</p>
				<ul class="theme-toggle-hint__options">
					<li v-for="opt in modeOptions" :key="opt.id">
						<button
							type="button"
							class="theme-option"
							:class="{ 'theme-option--active': colorMode === opt.id }"
							@click="pickMode(opt.id)"
						>
							<span class="theme-option__icon" :class="`theme-option__icon--${opt.id}`">
								<i class="la" :class="opt.icon" />
							</span>
							<span class="theme-option__body">
								<span class="theme-option__label">{{ opt.label }}</span>
								<span class="theme-option__desc">{{ opt.desc }}</span>
							</span>
							<i v-if="colorMode === opt.id" class="la la-check theme-option__check" aria-hidden="true" />
						</button>
					</li>
				</ul>
				<button type="button" class="theme-toggle-hint__dismiss" @click="dismissHint">
					Got it
				</button>
			</div>
		</transition>
		<button
			type="button"
			class="btn-theme-toggle"
			:class="{ 'btn-theme-toggle--highlight': showHint }"
			:aria-label="ariaLabel"
			v-tooltip="tooltip"
			@click="onToggleClick"
		>
			<i class="la" :class="iconClass" />
		</button>
	</div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { isThemeHintSeen, markThemeHintSeen } from "@/lib/theme";
import { useThemeStore } from "@/stores/theme";

const TOOLTIPS = {
	light: "Light mode (click for soft mode)",
	medium: "Soft mode — easy on the eyes (click for dark mode)",
	dark: "Dark mode (click for light mode)",
};

const ICONS = {
	light: "la-sun",
	medium: "la-adjust",
	dark: "la-moon",
};

const MODE_OPTIONS = [
	{
		id: "light",
		icon: "la-sun",
		label: "Light",
		desc: "Bright and clean — great for daytime work",
	},
	{
		id: "medium",
		icon: "la-adjust",
		label: "Soft",
		desc: "Balanced contrast — gentle on the eyes",
	},
	{
		id: "dark",
		icon: "la-moon",
		label: "Dark",
		desc: "Dimmed colors — comfortable in low light",
	},
];

export default {
	name: "ThemeToggle",
	data() {
		return {
			showHint: false,
			modeOptions: MODE_OPTIONS,
		};
	},
	computed: {
		...mapState(useThemeStore, ["colorMode"]),
		iconClass() {
			return ICONS[this.colorMode] || ICONS.light;
		},
		tooltip() {
			return this.showHint ? "" : (TOOLTIPS[this.colorMode] || TOOLTIPS.light);
		},
		ariaLabel() {
			return TOOLTIPS[this.colorMode] || TOOLTIPS.light;
		},
	},
	mounted() {
		if (!isThemeHintSeen()) {
			this.showHint = true;
		}
	},
	methods: {
		...mapActions(useThemeStore, ["setColorMode", "toggleColorMode"]),
		dismissHint() {
			markThemeHintSeen();
			this.showHint = false;
		},
		pickMode(mode) {
			this.setColorMode(mode);
			this.dismissHint();
		},
		onToggleClick() {
			if (this.showHint) {
				this.dismissHint();
			}
			this.toggleColorMode();
		},
	},
};
</script>

<style lang="scss">
.theme-toggle-wrap {
	position: relative;
	display: inline-flex;
}

.theme-toggle-hint {
	position: absolute;
	top: calc(100% + 12px);
	right: 0;
	z-index: 300;
	width: min(340px, calc(100vw - 24px));
	padding: var(--ds-space-4);
	background: var(--ds-color-surface);
	border: 1px solid var(--ds-color-border);
	border-radius: var(--ds-radius-lg);
	box-shadow: var(--ds-shadow-lg);
	text-align: left;
	white-space: normal;

	&__arrow {
		position: absolute;
		top: -6px;
		right: 12px;
		width: 12px;
		height: 12px;
		background: var(--ds-color-surface);
		border-left: 1px solid var(--ds-color-border);
		border-top: 1px solid var(--ds-color-border);
		transform: rotate(45deg);
	}

	&__title {
		margin: 0 0 var(--ds-space-1);
		font-size: var(--ds-font-size-sm);
		font-weight: var(--ds-font-weight-semibold);
		color: var(--ds-color-text);
	}

	&__lead {
		margin: 0 0 var(--ds-space-3);
		font-size: var(--ds-font-size-xs);
		line-height: var(--ds-line-height);
		color: var(--ds-color-text-muted);
	}

	&__options {
		margin: 0 0 var(--ds-space-3);
		padding: 0;
		list-style: none;
		display: flex;
		flex-direction: column;
		gap: var(--ds-space-2);
	}

	&__dismiss {
		display: block;
		width: 100%;
		height: 36px;
		font-size: var(--ds-font-size-xs);
		font-weight: var(--ds-font-weight-semibold);
		font-family: inherit;
		color: var(--ds-color-primary-text);
		background: var(--ds-color-primary);
		border: none;
		border-radius: var(--ds-radius-sm);
		cursor: pointer;
		transition: background 0.15s;

		&:hover {
			background: var(--ds-color-primary-hover);
		}
	}
}

.theme-option {
	display: flex;
	align-items: flex-start;
	gap: var(--ds-space-3);
	width: 100%;
	padding: var(--ds-space-3);
	text-align: left;
	white-space: normal;
	background: var(--ds-color-bg);
	border: 1px solid var(--ds-color-border-light);
	border-radius: var(--ds-radius-md);
	cursor: pointer;
	transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;

	&:hover {
		border-color: var(--ds-color-border);
		background: var(--ds-color-surface-hover);
	}

	&--active {
		border-color: var(--ds-color-primary);
		box-shadow: 0 0 0 2px var(--ds-color-primary-subtle);
	}

	&__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: 32px;
		height: 32px;
		font-size: 16px;
		border-radius: var(--ds-radius-full);

		&--light {
			color: #b45309;
			background: #fef3c7;
		}

		&--medium {
			color: #4b5563;
			background: #e5e7eb;
		}

		&--dark {
			color: #e5e7eb;
			background: #374151;
		}
	}

	&__body {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 3px;
	}

	&__label {
		font-size: var(--ds-font-size-xs);
		font-weight: var(--ds-font-weight-semibold);
		color: var(--ds-color-text);
	}

	&__desc {
		font-size: 11px;
		line-height: 1.45;
		color: var(--ds-color-text-muted);
		white-space: normal;
	}

	&__check {
		flex-shrink: 0;
		margin-top: 2px;
		font-size: 14px;
		color: var(--ds-color-primary-hover);
	}
}

.btn-theme-toggle--highlight {
	box-shadow: 0 0 0 3px var(--ds-color-primary-subtle);
}

.theme-hint-fade-enter-active,
.theme-hint-fade-leave-active {
	transition: opacity 0.2s ease, transform 0.2s ease;
}

.theme-hint-fade-enter-from,
.theme-hint-fade-leave-to {
	opacity: 0;
	transform: translateY(-6px);
}
</style>
